import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, unref as _unref, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "flex items-center justify-between mb-6 gap-4 rc-header" }
const _hoisted_2 = { class: "min-w-0" }
const _hoisted_3 = { class: "text-lg font-semibold text-black" }
const _hoisted_4 = { class: "flex items-center gap-4 ml-auto rc-controls" }
const _hoisted_5 = { class: "flex space-x-1 bg-gray-100 rounded-lg p-1 rc-periods" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "flex items-center gap-2 rc-date" }
const _hoisted_8 = { class: "flex items-center px-3 py-1.5 text-sm bg-white rc-datebox" }
const _hoisted_9 = { class: "h-80 rc-chart-container relative" }
const _hoisted_10 = {
  key: 0,
  class: "absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-10 rounded-lg"
}

import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { Line } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'
import type { ChartDataset, TooltipItem } from 'chart.js'
import { RevenueChartProps, RevenueChartEmits, ChartPeriod } from '@/types'
import { BaseInput } from '@/components'


export default /*@__PURE__*/_defineComponent({
  __name: 'RevenueChart',
  props: {
    currentPeriodData: { default: () => [] },
    lastYearData: { default: () => [] },
    labels: { default: () => [] },
    allowedPeriods: {},
    isLoading: { type: Boolean, default: false },
    frameless: { type: Boolean, default: false }
  },
  emits: ["periodChange", "dateRangeChange"],
  setup(__props: any, { emit: __emit }) {

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
)

// Props
const props = __props

// État local
const selectedPeriod = ref('jour')

// Événements
const emit = __emit

// Mobile detection (used for chart tick autoSkip and responsive options)
const isMobile = ref(false)
const isTablet = ref(false)
const isSmallScreen = ref(false)
const updateScreenSize = () => {
  const width = window.innerWidth
  isMobile.value = width <= 768
  isTablet.value = width > 768 && width <= 1024
  isSmallScreen.value = width <= 480
}
onMounted(() => {
  updateScreenSize()
  window.addEventListener('resize', updateScreenSize)
  // Initialiser les dates selon la période sélectionnée
  initializeDates()
})
onBeforeUnmount(() => window.removeEventListener('resize', updateScreenSize))

// Périodes disponibles (complètes)
const periods: ChartPeriod[] = [
  { value: 'jour', label: 'Jour' },
  { value: 'semaine', label: 'Semaine' },
  { value: 'mois', label: 'Mois' }
]

// Date range state (simple native inputs)
const today = new Date()
const toISO = (d: Date) => d.toISOString().slice(0, 10)

// Helper pour calculer le début de la semaine ISO
const startOfISOWeek = (d: Date): Date => {
  const date = new Date(d)
  const day = date.getUTCDay()
  const diff = date.getUTCDate() - day + (day === 0 ? -6 : 1) // Ajuster pour lundi
  date.setUTCDate(diff)
  date.setUTCHours(0, 0, 0, 0)
  return date
}

// Fonction pour calculer les dates selon la période (correspondant à getRangeConfig)
const getDatesForPeriod = (period: string): { start: string; end: string } => {
  const now = new Date()
  const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  let start: Date
  let end: Date

  switch (period) {
    case 'semaine': {
      // 13 semaines ISO : de 13 semaines avant jusqu'à la fin de la semaine en cours
      const currWeekStart = startOfISOWeek(new Date(todayDate))
      const nextWeekStart = new Date(currWeekStart)
      nextWeekStart.setUTCDate(nextWeekStart.getUTCDate() + 7)
      // Fin = dernier jour de la semaine en cours (dimanche)
      end = new Date(nextWeekStart)
      end.setUTCDate(end.getUTCDate() - 1)
      // Début = il y a 13 semaines
      start = new Date(nextWeekStart)
      start.setUTCDate(start.getUTCDate() - 7 * 13)
      break
    }
    case 'mois': {
      // 4 mois : du premier jour du mois il y a 3 mois jusqu'au dernier jour du mois en cours
      end = new Date(todayDate.getFullYear(), todayDate.getMonth() + 1, 0) // Dernier jour du mois en cours
      start = new Date(todayDate.getFullYear(), todayDate.getMonth() - 3, 1) // Premier jour il y a 3 mois
      break
    }
    case 'jour':
    default: {
      // 14 jours : aujourd'hui inclus, remonter 13 jours en arrière
      end = new Date(todayDate)
      start = new Date(todayDate)
      start.setDate(start.getDate() - 13) // Il y a 13 jours (14 jours au total)
      break
    }
  }

  return {
    start: toISO(start),
    end: toISO(end)
  }
}

// Initialiser les dates selon la période sélectionnée
const initializeDates = () => {
  const dates = getDatesForPeriod(selectedPeriod.value)
  dateStart.value = dates.start
  dateEnd.value = dates.end
}

// Laisser les dates vides au départ, elles seront initialisées au montage
const dateStart = ref('')
const dateEnd = ref('')

// When labels are loaded, sync the initial date range to the chart range (unless user changed it)
const hasUserChangedRange = ref(false)
const isSyncingFromLabels = ref(false)

const parseLabelDateToISO = (label: string): string | null => {
  const v = (label || '').trim()
  if (!v) return null

  // Already ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v

  // DD/MM/YY or DD/MM/YYYY
  const m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/)
  if (m) {
    const dd = Number(m[1])
    const mm = Number(m[2])
    const yyRaw = m[3]
    const yyyy = yyRaw.length === 2 ? 2000 + Number(yyRaw) : Number(yyRaw)
    if (yyyy >= 1900 && mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) {
      return `${String(yyyy).padStart(4, '0')}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`
    }
  }

  return null
}

// Helpers + constraints
const nextDay = (d: Date) => {
  const n = new Date(d)
  n.setDate(n.getDate() + 1)
  return n
}

// End must be strictly after start
const minEndDate = computed(() => {
  if (!dateStart.value) return ''
  const s = new Date(dateStart.value)
  return toISO(nextDay(s))
})

// Start must be strictly before end → max start = end - 1 day
const maxStartDate = computed(() => {
  if (!dateEnd.value) return ''
  const e = new Date(dateEnd.value)
  const prev = new Date(e)
  prev.setDate(prev.getDate() - 1)
  return toISO(prev)
})

const daysBetween = computed(() => {
  const s = new Date(dateStart.value)
  const e = new Date(dateEnd.value)
  const ms = e.getTime() - s.getTime()
  return Math.floor(ms / (1000 * 60 * 60 * 24)) + 1
})

// Nombre de mois complets entre les deux dates
const monthsBetween = computed(() => {
  if (!dateStart.value || !dateEnd.value) return 0
  const s = new Date(dateStart.value)
  const e = new Date(dateEnd.value)
  let months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth())
  if (e.getDate() < s.getDate()) months -= 1
  return Math.max(0, months)
})

// Local allowed periods based on range length + règle > 5 mois => seulement "mois"
const localAllowed = computed<Array<'jour' | 'semaine' | 'mois'>>(() => {
  if (!dateStart.value || !dateEnd.value || Number.isNaN(daysBetween.value)) {
    return ['jour', 'semaine', 'mois']
  }
  if (monthsBetween.value > 5) return ['mois']
  return daysBetween.value <= 31 ? ['jour', 'semaine'] : ['jour', 'semaine', 'mois']
})

// Périodes visibles selon allowedPeriods (prop has priority) or local range
const visiblePeriods = computed<ChartPeriod[]>(() => {
  const source = (props.allowedPeriods && props.allowedPeriods.length > 0)
      ? props.allowedPeriods
      : localAllowed.value
  const set = new Set(source)
  return periods.filter(p => set.has(p.value as 'jour' | 'semaine' | 'mois'))
})

// Données du graphique
const chartData = computed(() => {
  if (!props.currentPeriodData.length || !props.labels.length) {
    return null
  }

  const datasets: ChartDataset<'line'>[] = [
    {
      label: 'Période en cours',
      data: props.currentPeriodData,
      borderColor: '#2563eb',
      backgroundColor: '#2563eb',
      borderWidth: 2,
      fill: false,
      tension: 0.1, // Réduit pour des lignes moins arrondies et plus uniformes
      pointBackgroundColor: '#ffffff',
      pointBorderColor: '#2563eb',
      pointBorderWidth: 1.5,
      pointRadius: 0, // Points invisibles par défaut pour éviter le brouillon
      pointHoverRadius: 5, // Visibles uniquement au survol
      pointHoverBackgroundColor: '#ffffff',
      pointHoverBorderColor: '#2563eb',
      clip: false as any,
      order: 1
    }
  ]

  if (props.lastYearData.length) {
    datasets.push({
      label: 'Période précédente',
      data: props.lastYearData,
      borderColor: '#6B7280',
      backgroundColor: '#6B7280',
      borderWidth: 2,
      fill: false,
      tension: 0.1, // Réduit pour des lignes moins arrondies et plus uniformes
      borderDash: [6, 4],
      pointBackgroundColor: '#ffffff',
      pointBorderColor: '#6B7280',
      pointBorderWidth: 1.5,
      pointRadius: 0, // Points invisibles par défaut pour éviter le brouillon
      pointHoverRadius: 5, // Visibles uniquement au survol
      pointHoverBackgroundColor: '#ffffff',
      pointHoverBorderColor: '#6B7280',
      spanGaps: true,
      clip: false as any,
      order: 2
    })
  }

  return {
    labels: props.labels,
    datasets
  }
})

// Titre dynamique selon la période sélectionnée
const headerTitle = computed(() => {
  const map: Record<string, string> = {
    jour: "Chiffre d'affaires (jour)",
    semaine: "Chiffre d'affaires (semaine)",
    mois: "Chiffre d'affaires (mois)"
  }
  return map[selectedPeriod.value] ?? "Vue d'ensemble"
})

// Émettre le changement de période vers le parent (HomeView)
watch(selectedPeriod, async (val) => {
  // Mettre à jour les dates selon la nouvelle période
  const dates = getDatesForPeriod(val)
  // Marquer qu'on est en train de changer de période pour éviter la synchronisation depuis les labels
  hasUserChangedRange.value = false // Réinitialiser car on change de période
  isSyncingFromLabels.value = true
  dateStart.value = dates.start
  dateEnd.value = dates.end
  await nextTick()
  isSyncingFromLabels.value = false
  emit('periodChange', val)
})

// Si les périodes visibles changent, s'assurer que la période sélectionnée reste valide
watch(visiblePeriods, (list) => {
  if (!list.find(p => p.value === selectedPeriod.value) && list.length) {
    selectedPeriod.value = list[0].value
  }
})

// Émettre le changement de date range (YYYY-MM-DD)
const isValidRange = computed(() => {
  if (!dateStart.value || !dateEnd.value) return false
  const s = new Date(dateStart.value)
  const e = new Date(dateEnd.value)
  // Règle: la date de fin doit être strictement postérieure à la date de début
  return !Number.isNaN(s.getTime()) && !Number.isNaN(e.getTime()) && e > s
})

// Corriger en direct: si fin <= début, ajuster fin à début + 1 jour
watch([dateStart, dateEnd], () => {
  if (!dateStart.value || !dateEnd.value) return
  const s = new Date(dateStart.value)
  const e = new Date(dateEnd.value)
  if (e <= s) {
    dateEnd.value = toISO(nextDay(s))
  }
})

watch([dateStart, dateEnd], ([s, e], [ps, pe]) => {
  if (isSyncingFromLabels.value) return
  // Ignore first run (previous values undefined)
  if (ps === undefined || pe === undefined) return
  if (s !== ps || e !== pe) {
    hasUserChangedRange.value = true
  }
})

watch(
  () => props.labels,
  async (labels) => {
    // Ne pas synchroniser si l'utilisateur a modifié les dates manuellement
    if (hasUserChangedRange.value) return
    // Ne pas synchroniser si on est en train de synchroniser depuis les labels
    if (isSyncingFromLabels.value) return
    // Ne pas synchroniser si les dates du filtre sont déjà définies
    // (pour respecter les dates du filtre de période)
    if (dateStart.value && dateEnd.value) return
    if (!labels || labels.length < 2) return

    const first = parseLabelDateToISO(labels[0])
    const last = parseLabelDateToISO(labels[labels.length - 1])
    if (!first || !last) return

    // Only sync if it would actually change something (avoid loops)
    if (dateStart.value === first && dateEnd.value === last) return

    isSyncingFromLabels.value = true
    dateStart.value = first
    dateEnd.value = last
    await nextTick()
    isSyncingFromLabels.value = false
  },
  { immediate: true }
)

watch([dateStart, dateEnd], ([s, e], [ps, pe]) => {
  // Ne pas émettre si on est en train de synchroniser depuis les labels
  if (isSyncingFromLabels.value) return
  // Ne pas émettre si les valeurs n'ont pas changé
  if (s === ps && e === pe) return
  // Ne pas émettre si la plage n'est pas valide
  if (!isValidRange.value) return
  emit('dateRangeChange', dateStart.value, dateEnd.value)
})

// Options du graphique (responsive selon la taille d'écran)
const chartOptions = computed(() => {
  const fontSize = isSmallScreen.value ? 10 : isMobile.value ? 11 : 12
  const maxTicksLimit = isSmallScreen.value ? 4 : isMobile.value ? 6 : isTablet.value ? 8 : 999
  
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        padding: isSmallScreen.value ? 8 : 12,
        titleFont: {
          size: isSmallScreen.value ? 12 : 14
        },
        bodyFont: {
          size: isSmallScreen.value ? 11 : 13
        },
        callbacks: {
          title(items: any) {
            return items?.[0]?.label ?? ''
          },
          label(context: TooltipItem<'line'>) {
            const value = context.parsed.y ?? 0
            return `${context.dataset.label}: ${value.toLocaleString('fr-FR')} €`
          },
          labelColor(context: TooltipItem<'line'>) {
            const isPrev = context.datasetIndex === 1
            const color = isPrev ? '#6B7280' : '#2563eb'
            return { borderColor: color, backgroundColor: '#ffffff', borderWidth: 2 }
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: '#6b7280',
          font: { 
            size: fontSize,
            weight: isMobile.value ? 'normal' : 'normal'
          },
          autoSkip: selectedPeriod.value === 'jour' ? false : (isMobile.value || isTablet.value),
          maxTicksLimit: selectedPeriod.value === 'jour' ? undefined : maxTicksLimit,
          maxRotation: 0,
          minRotation: 0,
          padding: isSmallScreen.value ? 4 : isMobile.value ? 6 : 8,
          callback(value: any, index: number) {
            try {
              const labels: string[] = (chartData as any)?.value?.labels ?? props.labels ?? []
              const label = labels[index]
              if (!label) return ''
              const dateMatch = label.match(/^(\d{2})\/(\d{2})\/(\d{2}|\d{4})$/)
              if (dateMatch) {
                const [, dayLabel, monthLabel, yearLabel] = dateMatch
                const shortLabel = isMobile.value ? `${dayLabel}/${monthLabel}` : `${dayLabel}/${monthLabel}/${yearLabel.slice(-2)}`
                if (selectedPeriod.value === 'jour') {
                  const total = labels.length
                  const shouldThinTicks = isMobile.value || isTablet.value || total > 14
                  if (shouldThinTicks) {
                    const targetTicks = isSmallScreen.value ? 3 : isMobile.value ? 4 : isTablet.value ? 6 : 10
                    const step = Math.max(1, Math.ceil((total - 1) / Math.max(1, targetTicks - 1)))
                    const middleIndex = Math.round((total - 1) / 2)
                    return index === 0 || index === middleIndex || index === total - 1 || index % step === 0
                      ? shortLabel
                      : ''
                  }
                  return shortLabel
                }
                // Pour les autres modes, n'afficher que les lundis
                const fullYear = yearLabel.length === 2 ? `20${yearLabel}` : yearLabel
                const d = new Date(`${fullYear}-${monthLabel}-${dayLabel}T00:00:00`) // Reconstruire la date complète
                const dayOfWeek = d.getUTCDay()
                return dayOfWeek === 1 ? shortLabel : ''
              }
              // Vérifier si c'est un mois au format MM/YY
              const isMonth = /^\d{2}\/\d{2}$/.test(label)
              if (isMonth) {
                return label
              }
              // Vérifier si c'est une date avec semaine au format DD/MM/YY-Sn
              const isWeekDate = /^\d{2}\/\d{2}\/\d{2}-S\d+$/.test(label)
              if (isWeekDate) {
                return label
              }
              // Pour les autres formats, retourner tel quel
              return label
            } catch {
              return ''
            }
          }
        }
      },
      y: {
        grid: { 
          color: '#f3f4f6', 
          borderColor: '#e5e7eb',
          lineWidth: isMobile.value ? 0.5 : 1
        },
        ticks: {
          color: '#6b7280',
          font: { 
            size: fontSize,
            weight: isMobile.value ? 'normal' : 'normal'
          },
          padding: isSmallScreen.value ? 4 : isMobile.value ? 6 : 8,
          maxTicksLimit: isSmallScreen.value ? 4 : isMobile.value ? 5 : 8,
          callback(value: number | string) {
            const numericValue = typeof value === 'number' ? value : Number(value)
            if (Number.isNaN(numericValue)) return `${value} €`
            // Sur très petits écrans, simplifier l'affichage
            if (isSmallScreen.value && numericValue >= 1000) {
              return `${(numericValue / 1000).toFixed(1)}k €`
            }
            return `${numericValue.toLocaleString('fr-FR')} €`
          }
        }
      }
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false
    },
    elements: {
      point: {
        hoverBackgroundColor: '#ffffff',
        hoverBorderColor: '#2563eb',
        hoverBorderWidth: isMobile.value ? 2 : 3,
        radius: isMobile.value ? 0 : 0,
        hoverRadius: isSmallScreen.value ? 4 : isMobile.value ? 5 : 5
      },
      line: {
        borderWidth: isMobile.value ? 1.5 : 2
      }
    }
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass([
      props.frameless
        ? 'bg-transparent p-0 border-0 shadow-none rounded-none rc-main-container rc-frameless'
        : 'bg-white p-6 border-0 shadow-none rounded-none rc-main-container'
    ])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("h3", _hoisted_3, _toDisplayString(headerTitle.value), 1)
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visiblePeriods.value, (period) => {
            return (_openBlock(), _createElementBlock("button", {
              key: period.value,
              onClick: ($event: any) => (selectedPeriod.value = period.value),
              class: _normalizeClass([
              'px-3 py-1.5 text-sm font-medium rounded-md transition-colors rc-period-btn',
              selectedPeriod.value === period.value
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-black hover:text-black'
            ])
            }, _toDisplayString(period.label), 11, _hoisted_6))
          }), 128))
        ]),
        _createElementVNode("div", _hoisted_7, [
          _cache[4] || (_cache[4] = _createElementVNode("div", { class: "text-sm font-medium text-black rc-date-label" }, "Période", -1)),
          _createElementVNode("div", _hoisted_8, [
            _cache[2] || (_cache[2] = _createElementVNode("i", { class: "fas fa-calendar-alt text-black mr-2 rc-calendar-icon" }, null, -1)),
            _createVNode(_unref(BaseInput), {
              modelValue: dateStart.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((dateStart).value = $event)),
              type: "date",
              "custom-class": "w-36 border-0 focus:ring-0 p-0 rc-date-input",
              max: maxStartDate.value
            }, null, 8, ["modelValue", "max"]),
            _cache[3] || (_cache[3] = _createElementVNode("span", { class: "mx-2 text-black rc-date-separator" }, "À", -1)),
            _createVNode(_unref(BaseInput), {
              modelValue: dateEnd.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((dateEnd).value = $event)),
              type: "date",
              "custom-class": "w-36 border-0 focus:ring-0 p-0 rc-date-input",
              min: minEndDate.value
            }, null, 8, ["modelValue", "min"])
          ])
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_9, [
      (props.isLoading || !chartData.value || (props.currentPeriodData.length === 0 && props.labels.length === 0))
        ? (_openBlock(), _createElementBlock("div", _hoisted_10, _cache[5] || (_cache[5] = [
            _createElementVNode("div", { class: "flex flex-col items-center gap-3" }, [
              _createElementVNode("div", { class: "animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" }),
              _createElementVNode("span", { class: "text-sm font-medium text-black" }, "Chargement du graphique...")
            ], -1)
          ])))
        : _createCommentVNode("", true),
      (chartData.value && !props.isLoading && props.currentPeriodData.length > 0 && props.labels.length > 0)
        ? (_openBlock(), _createBlock(_unref(Line), {
            key: 1,
            data: chartData.value,
            options: chartOptions.value,
            class: "w-full h-full"
          }, null, 8, ["data", "options"]))
        : _createCommentVNode("", true)
    ]),
    _cache[6] || (_cache[6] = _createStaticVNode("<div class=\"mt-5 flex items-center justify-center gap-3 rc-legend\" data-v-9997577c><div class=\"inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5\" data-v-9997577c><span class=\"relative inline-flex h-3 w-9 items-center\" data-v-9997577c><span class=\"h-0.5 w-full rounded-full bg-blue-600\" data-v-9997577c></span><span class=\"absolute left-1/2 h-2.5 w-2.5 -translate-x-1/2 rounded-full border-2 border-blue-600 bg-white\" data-v-9997577c></span></span><span class=\"text-sm font-medium text-blue-700\" data-v-9997577c>Période en cours</span></div><div class=\"inline-flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1.5\" data-v-9997577c><span class=\"relative inline-flex h-3 w-9 items-center\" data-v-9997577c><span class=\"w-full border-t-2 border-dashed border-slate-500\" data-v-9997577c></span><span class=\"absolute left-1/2 h-2.5 w-2.5 -translate-x-1/2 rounded-full border-2 border-slate-500 bg-white\" data-v-9997577c></span></span><span class=\"text-sm font-medium text-slate-700\" data-v-9997577c>Période précédente</span></div></div>", 1))
  ], 2))
}
}

})